import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Button,
  Typography,
} from '@mui/material';
import { convertTimestampForFormControl } from '../../../../utils/date_convert';
import COLORS from '../../../../resources/style/Themes';

export default function EditAppVersionModal({
  show,
  onHide,
  versionData,
  onSave,
}) {
  const [version, setVersion] = useState('');
  const [required, setRequired] = useState(false);
  const [rolloutTimestamp, setRolloutTimestamp] = useState(null);
  const [goLive, setGoLive] = useState(false);
  const [notificationDays, setNotificationDays] = useState([]);
  const [isAddingDay, setIsAddingDay] = useState(false);
  const [newDay, setNewDay] = useState('');
  const [updateComplete, setUpdateComplete] = useState(false);
  const [objectType, setObjectType] = useState('');
  const [id, setId] = useState('');
  const [validation, setValidation] = useState({});

  useEffect(() => {
    if (versionData) {
      setVersion(versionData.version || '');
      setRequired(versionData.required || false);
      setRolloutTimestamp(versionData.rollout_timestamp || null);
      setObjectType(versionData.object_type || '');
      setGoLive(versionData.go_live || false);
      setNotificationDays(versionData.notification_days || []);
      setUpdateComplete(versionData.update_complete || false);
      setId(versionData.id || '');
    }
  }, [versionData, show]);

  const handleSubmit = () => {
    if (!validateFields()) {
      return;
    }

    const epochRolloutTimestamp = rolloutTimestamp
      ? new Date(rolloutTimestamp).getTime()
      : null;

    const editedAppVersion = {
      notification_days: notificationDays,
      required: required,
      rollout_timestamp: epochRolloutTimestamp,
      version: parseFloat(version),
      go_live: goLive,
      update_complete: updateComplete,
      object_type: objectType,
      id: id,
    };

    setValidation({});
    onSave(editedAppVersion);
    onHide();
  };

  const handleAddDay = () => {
    setIsAddingDay(true);
  };

  const handleSaveDay = () => {
    const dayInt = parseInt(newDay.trim(), 10);

    if (!isNaN(dayInt)) {
      if (!notificationDays.includes(dayInt)) {
        setNotificationDays([...notificationDays, dayInt]);
        setNewDay('');
      } else {
        setNewDay('');
      }
    } else {
      setNewDay('');
    }

    setIsAddingDay(false);
  };

  const handleDeleteDay = (idx) => {
    const updatedDays = notificationDays.filter((_, index) => index !== idx);
    setNotificationDays(updatedDays);
  };

  const validateFields = () => {
    let isValid = true;
    const newValidation = {};
    if (!version) {
      newValidation.version = 'error';
      isValid = false;
    }

    if (!notificationDays || notificationDays.length === 0) {
      newValidation.notificationDays = 'error';
      isValid = false;
    }

    if (
      !rolloutTimestamp ||
      (rolloutTimestamp instanceof Date && isNaN(rolloutTimestamp.getTime()))
    ) {
      newValidation.rolloutTimestamp = 'error';
      isValid = false;
    }

    setValidation(newValidation);
    return isValid;
  };

  return (
    <Dialog open={show} onClose={onHide} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
      >
        Edit {version}
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Version"
          fullWidth
          error={validation.version === 'error'}
          helperText={
            validation.version === 'error'
              ? 'Please enter a valid version number (e.g., 1.2).'
              : ''
          }
          value={version}
          onChange={(e) => {
            const value = e.target.value;
            const regex = /^\d*\.?\d*$/;
            if (regex.test(value)) {
              setVersion(value);
            }
          }}
          margin="normal"
        />

        {/* Rollout Timestamp Label */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 500, marginTop: '16px' }}
        >
          Rollout Timestamp (Required)
        </Typography>
        <>
          <TextField
            label="Rollout Date (PST)"
            type="datetime-local"
            fullWidth
            value={
              rolloutTimestamp
                ? convertTimestampForFormControl(rolloutTimestamp)
                : ''
            }
            onChange={(e) => {
              const newDate = new Date(e.target.value);
              setRolloutTimestamp(newDate);
            }}
            error={validation.rolloutTimestamp === 'error'}
            helperText={
              validation.rolloutTimestamp === 'error'
                ? 'Please enter a date for rollout.'
                : ''
            }
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </>

        {/* Notification Days Label */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 500, marginTop: '16px' }}
        >
          Notification Days (Required)
        </Typography>
        <>
          <div>
            {notificationDays.map((num, idx) => (
              <Chip
                key={idx}
                label={num}
                style={{ marginRight: '5px', marginTop: '2px' }}
                onDelete={() => handleDeleteDay(idx)}
              />
            ))}
            {isAddingDay ? (
              <TextField
                size="small"
                value={newDay}
                type="number"
                onChange={(e) => setNewDay(e.target.value)}
                onBlur={handleSaveDay}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveDay();
                  }
                }}
                autoFocus
                style={{ verticalAlign: 'bottom' }}
              />
            ) : (
              <Chip
                label={'+ Add'}
                onClick={handleAddDay}
                style={{ marginLeft: '5px' }}
              />
            )}
            {validation.notificationDays === 'error' && (
              <div
                style={{
                  color: '#d32f2f',
                  fontSize: '0.75rem',
                  marginTop: '4px',
                }}
              >
                At least one notification day is required.
              </div>
            )}
          </div>
        </>

        <TextField
          select
          label="Required"
          fullWidth
          value={String(required)}
          onChange={(e) => setRequired(e.target.value === 'true')}
          SelectProps={{
            native: true,
          }}
          margin="normal"
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </TextField>

        <TextField
          select
          label="Go Live"
          fullWidth
          value={String(goLive)}
          onChange={(e) => setGoLive(e.target.value === 'true')}
          SelectProps={{
            native: true,
          }}
          margin="normal"
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </TextField>

        <TextField
          select
          label="Update Complete"
          fullWidth
          value={String(updateComplete)}
          onChange={(e) => setUpdateComplete(e.target.value === 'true')}
          SelectProps={{
            native: true,
          }}
          margin="normal"
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} variant="contained" color="secondary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
